/**
 * 🔒 B171 / M-R5 — Minimização do exemplo few-shot do MECR-RAG
 *
 * O circuito MECR-RAG reusa análises aprovadas de OUTROS pacientes como
 * exemplo de raciocínio na geração da análise do paciente atual
 * (`buscarAnalisesAprovadas`). Os campos de texto livre dessas análises
 * (principais achados, sugestões de conduta, diagnóstico) podem conter
 * identificadores diretos (nome) ou quase-identificadores (datas, números de
 * prontuário/RGCT/CPF/telefone) do paciente-terceiro — que, numa população
 * pequena e regional, elevam o risco de reidentificação por combinação de
 * atributos (LGPD Art. 11; risco R5 / medida M-R5 da AIA).
 *
 * Esta minimização redige esses identificadores:
 *   - na GRAVAÇÃO da análise (`salvarAnalise` grava `mecrExemplo` já limpo,
 *     usando o nome do próprio paciente — único ponto onde o conhecemos);
 *   - na LEITURA, como rede de segurança para o histórico já gravado
 *     (`buscarAnalisesAprovadas`), antes de o exemplo cruzar para o prompt de
 *     outro paciente → subprocessador.
 *
 * É deliberadamente CONSERVADORA: preserva o conteúdo clínico que dá valor
 * pedagógico ao exemplo (achados, condutas, valores laboratoriais crus, idade)
 * e redige apenas o que é identificador forte. A agressividade é calibrável
 * (AÇÃO de calibração registrada no B171: Leonardo lê análises reais e ajusta).
 *
 * NÃO substitui `anonimizarDadosPaciente` — aquela opera sobre o INPUT
 * estruturado enviado à IA; aqui o alvo é o TEXTO LIVRE de SAÍDA reaproveitado
 * como few-shot.
 */

const PLACEHOLDER_DATA = "[data omitida]";
const PLACEHOLDER_ID = "[id omitido]";
const PLACEHOLDER_NOME = "[nome omitido]";

// Datas — quase-identificador forte e de baixa perda clínica num exemplo:
const RE_DATA_ISO = /\b\d{4}-\d{2}-\d{2}\b/g; // 2024-03-31
const RE_DATA_NUMERICA = /\b\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}\b/g; // 31/03/2024
const RE_DATA_EXTENSO =
  /\b\d{1,2}\s+de\s+(?:janeiro|fevereiro|mar[çc]o|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)(?:\s+de\s+\d{2,4})?\b/gi;

// Identificadores FORMATADOS (CPF, telefone, prontuário/RGCT com separadores).
// Exigem separadores/pontuação típica de identificador → NÃO casam com valores
// laboratoriais crus (ex.: "plaquetas 95000", "MELD 18", "leucócitos 12500").
const RE_CPF = /\b\d{3}\.\d{3}\.\d{3}-?\d{2}\b/g; // 123.456.789-00
const RE_TELEFONE = /\(?\d{2}\)?\s?9?\d{4}[-\s]\d{4}\b/g; // (69) 99999-9999 / 3216-0000
const RE_ID_SEGMENTADO = /\b\d{2,}\.\d{2,}(?:\.\d{2,})+\b/g; // 12.345.678 (RGCT/prontuário)

function escaparRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Redige um nome (completo e cada parte com ≥3 letras) do texto, sem acento-
 * sensibilidade básica de caixa. Usado só na gravação, onde o nome é conhecido.
 */
function removerNomes(texto: string, nomesProibidos: string[]): string {
  let out = texto;
  for (const nomeCompleto of nomesProibidos) {
    if (!nomeCompleto || typeof nomeCompleto !== "string") continue;
    const partes = nomeCompleto
      .split(/\s+/)
      .map((p) => p.trim())
      .filter((p) => p.replace(/[^A-Za-zÀ-ÿ]/g, "").length >= 3);
    // Nome completo primeiro, depois cada parte significativa.
    const alvos = [nomeCompleto.trim(), ...partes].filter(Boolean);
    for (const alvo of alvos) {
      const re = new RegExp(`\\b${escaparRegex(alvo)}\\b`, "gi");
      out = out.replace(re, PLACEHOLDER_NOME);
    }
  }
  return out;
}

/**
 * Minimiza um texto livre de exemplo few-shot. Idempotente.
 * @param texto texto clínico de saída a minimizar
 * @param nomesProibidos nomes a redigir (ex.: [paciente.nome]); opcional
 */
export function minimizarTextoMECR(
  texto: string,
  nomesProibidos: string[] = []
): string {
  if (!texto || typeof texto !== "string") return texto ?? "";
  let out = texto;
  if (nomesProibidos.length > 0) out = removerNomes(out, nomesProibidos);
  out = out.replace(RE_CPF, PLACEHOLDER_ID);
  out = out.replace(RE_DATA_ISO, PLACEHOLDER_DATA);
  out = out.replace(RE_DATA_NUMERICA, PLACEHOLDER_DATA);
  out = out.replace(RE_DATA_EXTENSO, PLACEHOLDER_DATA);
  out = out.replace(RE_TELEFONE, PLACEHOLDER_ID);
  out = out.replace(RE_ID_SEGMENTADO, PLACEHOLDER_ID);
  return out;
}

/** Minimiza uma lista de strings (ex.: principaisAchados). */
export function minimizarListaMECR(
  itens: unknown,
  nomesProibidos: string[] = []
): string[] {
  if (!Array.isArray(itens)) return [];
  return itens
    .filter((i): i is string => typeof i === "string")
    .map((i) => minimizarTextoMECR(i, nomesProibidos));
}

/**
 * Extrai e minimiza o texto de uma sugestão de conduta, tolerando variações de
 * campo (descricao/texto/recomendacao) como faz `buscarAnalisesAprovadas`.
 */
export function minimizarSugestaoMECR(
  sugestao: any,
  nomesProibidos: string[] = []
): string {
  if (!sugestao) return "";
  const bruto =
    sugestao.descricao ||
    sugestao.texto ||
    sugestao.recomendacao ||
    (typeof sugestao === "string" ? sugestao : "");
  return minimizarTextoMECR(String(bruto || ""), nomesProibidos);
}
